import { Component, HostListener, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import { forkJoin } from 'rxjs';
import { reportSankeyOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
} from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { ReportVizChartColors } from '../constants';
import { Log } from 'src/app/logger.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SearchFilter } from 'ldt-people-api';
import { PrevNextFlowConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const MAX_COMPANIES = 10;

@Component({
  selector: 'app-reports-prev-next-flow',
  templateUrl: './prev-next-flow.component.html',
  styleUrls: ['./prev-next-flow.component.scss'],
  standalone: false,
})
export class PrevNextFlowComponent extends BaseReportComponent<PrevNextFlowConfig> {
  @Input() field: string;
  @Input() allowFieldSelection = true;
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() reportId: string;
  @Input() boxShadow: boolean = true;

  @Input()
  set selectedSeries(value: 'previous' | 'next' | 'both' | undefined) {
    if (value) {
      this._selectedSeries = value;
    }
  }
  get selectedSeries(): 'previous' | 'next' | 'both' {
    return this._selectedSeries;
  }

  protected readonly CHART_DATA_DESC_PREV =
    'Number of employees who joined over the provided date range grouped by their previous companies';
  protected readonly CHART_DATA_DESC_NEXT =
    'Number of employees who departed over the provided date range grouped by their next companies';
  @Input() showTitleText = true;

  protected readonly CHART_ID = 'arrivals_departures';
  private _selectedSeries: 'previous' | 'next' | 'both' = 'both';
  private SMALL_SCREEN_WIDTH = 550;
  private resizeTimeout: any;

  seriesOptions = [
    { value: 'both', displayText: 'Both' },
    { value: 'previous', displayText: 'Previous Company' },
    { value: 'next', displayText: 'Next Company' },
  ];

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedSeries', () => this._selectedSeries);
  }

  @HostListener('window:resize', [])
  onResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.updateChartTitle();
    }, 400); // 400ms debounce
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportSankeyOptions)
    );
    this.updateChartTitle();
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    if (!this.quickBuild) return;

    this.showLoading();

    const reportParams = {
      aggregate_type: 'range',
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: ['jobs.metadata.previous_job.company.name'],
      report_type: 'arrivals',
    };
    const reportParams2 = {
      aggregate_type: 'range',
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: ['jobs.metadata.next_job.company.name'],
      report_type: 'departures',
    };
    forkJoin([
      this.reportService.getReportDataWithReportDetails(
        this.reportId,
        this.CHART_ID,
        reportParams,
        this.reportTarget,
        this.quickBuild
      ),
      this.reportService.getReportDataWithReportDetails(
        this.reportId,
        this.CHART_ID,
        reportParams2,
        this.reportTarget,
        this.quickBuild
      ),
    ]).subscribe({
      next: ([prev_company_data, next_company_data]) => {
        Log.d('PrevNextFlowComponent getChartData', prev_company_data, next_company_data);
        const series = this.generateChartSeries(
          prev_company_data.data.arrivals_departures,
          next_company_data.data.arrivals_departures
        );
        this.updateChart(series);
        this.chartLoadingComplete();
      },
      error: (error) => {
        Log.e('PrevNextFlowComponent getChartData error', error);
        this.showChartError(error);
      },
    });
  }

  protected generateChartSeries(prevData: any, nextData: any): any[] {
    Log.d('PrevNextFlowComponent generateChartSeries', prevData, nextData);
    let data: Highcharts.SeriesSankeyPointOptionsObject[] = [];

    const includePrevious = this._selectedSeries === 'previous' || this._selectedSeries === 'both';
    const includeNext = this._selectedSeries === 'next' || this._selectedSeries === 'both';

    Log.d(
      'PrevNextFlowComponent generateChartSeries',
      this._selectedSeries,
      includePrevious,
      includeNext
    );

    if (includePrevious) {
      data = data.concat(
        prevData
          .filter((c: any) => c.group_values[0].value !== this.quickBuild.name)
          .slice(0, MAX_COMPANIES)
          .map((c: any, i: any) => {
            return {
              from: 'from-' + c.group_values[0].value,
              to: this.quickBuild.name,
              weight: c.arrivals,
              custom: {
                sourceCompanyLocation: 'to',
              },
            };
          })
      );
    }

    if (includeNext) {
      data = data.concat(
        nextData
          .filter((c: any) => c.group_values[0].value !== this.quickBuild.name)
          .slice(0, MAX_COMPANIES)
          .map((c: any, i: any) => {
            return {
              from: this.quickBuild.name,
              to: 'to-' + c.group_values[0].value,
              weight: c.departures,
              custom: {
                sourceCompanyLocation: 'from',
              },
            };
          })
      );
    }

    const nodeIds = Array.from(
      new Set([
        ...data.map((d) => d.from).filter((d) => d !== undefined),
        ...data.map((d) => d.to).filter((d) => d !== undefined),
      ])
    );
    const nodes = nodeIds.map((id) => {
      return {
        id: id,
        name: id!.replace('from-', '').replace('to-', ''),
      };
    });

    let sData: Highcharts.SeriesSankeyOptions[] = [
      {
        colors: ReportVizChartColors,
        type: 'sankey',
        data: data,
        nodes: nodes,
        name: 'Employee Flow',
      },
    ];

    return sData;
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    Log.d('PrevNextFlowComponent getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);
    const dateFilterArrivals: UIFilter = {
      filter: {
        field: 'jobs.started_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        date_from: this.dateFrom,
        date_to: this.dateTo,
        type: SearchFilter.TypeEnum.Must,
      },
    };
    const dateFilterDepartures: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: this.dateFrom,
        date_to: this.dateTo,
      },
    };

    const anyPoint = point as any; // Not all available properties are documented on the Point type
    const flowFields = this.getPointFlowFields(anyPoint);

    // Current company is a special case, we need to handle it differently
    if (flowFields.isCurrentCompany) {
      return this.getContextMenuItemsForCurrentCompany(
        anyPoint,
        qbCopy,
        dateFilterArrivals,
        dateFilterDepartures
      );
    }

    Log.d('PrevNextFlowComponent getContextMenuItems flowFields', flowFields);
    if (!flowFields.fieldName || !flowFields.stringValue) {
      return [];
    }

    const flowDirection =
      flowFields.fieldName === 'jobs.metadata.next_job.company.name' ? 'next' : 'previous';

    const filterField =
      flowDirection === 'next' ? 'jobs.next_job.company.name' : 'jobs.previous_job.company.name';

    const flowFilter: UIFilter = {
      filter: {
        field: filterField,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [flowFields.stringValue],
      },
    };

    const label =
      flowDirection === 'next'
        ? this.quickBuild.name + '  to ' + flowFields.stringValue
        : flowFields.stringValue + '  to ' + this.quickBuild.name;
    Log.d('PrevNextFlowComponent getContextMenuItems flowFilter', flowFilter);

    let dateFilter = dateFilterDepartures;
    if (flowDirection === 'previous') {
      dateFilter = dateFilterArrivals;
    }

    return [
      {
        label: `View People flow from ${label}`,
        icon: 'swap_horiz',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilter, flowFilter], 'moneyball');
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  // For the current company, we're going to gather all arrivals or departures or both over the date range, depending on the selected series
  private getContextMenuItemsForCurrentCompany(
    point: Highcharts.Point,
    qbCopy: QuickBuild,
    dateFilterArrivals: UIFilter,
    dateFilterDepartures: UIFilter
  ): ChartContextMenuItem[] {
    const prevItem = {
      label: `View People flow into ${qbCopy.name}`,
      icon: 'swap_horiz',
      action: (p: any) => {
        QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilterArrivals], 'moneyball');
        const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
        group!.positionStatus = PositionStatus.First;

        this.openDrawer(qbCopy);
      },
    };

    const nextItem = {
      label: `View People flow out of ${qbCopy.name}`,
      icon: 'swap_horiz',
      action: (p: any) => {
        QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilterDepartures], 'moneyball');
        const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
        group!.positionStatus = PositionStatus.Last;
        Log.d('PrevNextFlowComponent getContextMenuItemsForCurrentCompany', qbCopy);
        this.openDrawer(qbCopy);
      },
    };

    if (this._selectedSeries === 'previous') {
      return [prevItem];
    }

    if (this._selectedSeries === 'next') {
      return [nextItem];
    }

    return [prevItem, nextItem];
  }

  private getPointFlowFields(point: Highcharts.Point): {
    fieldName: string | null;
    stringValue: string | null;
    isCurrentCompany: boolean;
  } {
    const anyPoint = point as any; // Not all available properties are documented on the Point type

    let prevJobColNumber = 0;
    let currJobColNumber = 1;
    let nextJobColNumber = 2;

    // When next is selected, the clickable points move around
    if (this._selectedSeries === 'next') {
      prevJobColNumber = -1;
      currJobColNumber = 0;
      nextJobColNumber = 1;
    }

    // They clicked on an edge of the sankey, not a connector
    if ('column' in anyPoint && anyPoint.column !== null) {
      switch (anyPoint.column) {
        case prevJobColNumber:
          return {
            fieldName: 'jobs.metadata.previous_job.company.name',
            stringValue: anyPoint.name.replace('to-', '').replace('from-', ''),
            isCurrentCompany: false,
          };
        case nextJobColNumber:
          return {
            fieldName: 'jobs.metadata.next_job.company.name',
            stringValue: anyPoint.name.replace('to-', '').replace('from-', ''),
            isCurrentCompany: false,
          };
        case currJobColNumber:
          return {
            fieldName: '',
            stringValue: '',
            isCurrentCompany: true,
          };
        default:
          return {
            fieldName: null,
            stringValue: null,
            isCurrentCompany: false,
          };
      }
    }

    switch (anyPoint.custom?.sourceCompanyLocation) {
      case 'from':
        return {
          fieldName: 'jobs.metadata.next_job.company.name',
          stringValue: anyPoint.to.replace('to-', '').replace('from-', ''),
          isCurrentCompany: false,
        };
      case 'to':
        return {
          fieldName: 'jobs.metadata.previous_job.company.name',
          stringValue: anyPoint.from.replace('to-', '').replace('from-', ''),
          isCurrentCompany: false,
        };
      default:
        return {
          fieldName: null,
          stringValue: null,
          isCurrentCompany: false,
        };
    }
  }

  onSeriesChanged(value: string) {
    this._selectedSeries = value as 'previous' | 'next' | 'both';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  private updateChartTitle(): void {
    if (this.chart) {
      this.chart.update(
        {
          title: {
            text: this.showTitleText ? 'Top Company Talent Flow' : undefined,
            align: window.innerWidth <= this.SMALL_SCREEN_WIDTH ? 'left' : 'center',
          },
          yAxis: {
            title: { text: '# of employees' },
          },
        },
        true,
        true
      );
    }
  }

  ngOnDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    super.ngOnDestroy?.();
  }
}
